import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewChild } from '@angular/core';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { AlTableColumn, AlTableRowAction } from '../../components/features/table/table.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService, UserRoles } from '../../services/auth.service';
import { LearnerProfileDialogComponent } from '../../components/dialogs/learner-profile-dialog/learner-profile-dialog.component';
import { participantPseudonym } from '../../helpers/participant-pseudonym';
import { FeatureSet, FeatureSetService } from '../../services/feature-set.service';
import {
  CanonicalCourse,
  CanonicalTeacher,
  CanonicalUser,
  MoodleDashboardService,
  MoodleRefreshStatus,
} from '../../services/moodle-dashboard.service';
import { LmsRegistration, LmsRegistryService } from '../../services/lms-registry.service';
import { UserService } from '../../services/user.service';
import { CurriculumService } from '../../services/curriculum.service';
import { CourseResponsiblesDialogComponent } from '../../components/dialogs/course-responsibles-dialog/course-responsibles-dialog.component';
import { CoursePrerequisitesDialogComponent } from '../../components/dialogs/course-prerequisites-dialog/course-prerequisites-dialog.component';
import { MoodleDashboardCourseDetailComponent } from './detail-dialogs/course-detail.component';
import { ParsedCourseStructureDialogComponent } from './detail-dialogs/course-structure-dialog.component';
import { CourseDifficultyDialogComponent } from './detail-dialogs/course-difficulty-dialog.component';
import { MoodleDashboardUserDetailComponent } from './detail-dialogs/user-detail.component';

type MoodleDashboardTab = 'courses' | 'users' | 'teachers';
type MoodleDashboardColumnKey =
  | 'name'
  | 'short'
  | 'category'
  | 'id'
  | 'sync'
  | 'responsible'
  | 'learners'
  | 'action'
  | 'email'
  | 'enrol'
  | 'role'
  | 'actionWide';

type MoodleDashboardColumnWidths = Record<
  MoodleDashboardTab,
  Partial<Record<MoodleDashboardColumnKey, number>>
>;

interface MoodleColumnResizeState {
  tab: MoodleDashboardTab;
  key: MoodleDashboardColumnKey;
  nextKey: MoodleDashboardColumnKey;
  startX: number;
  tableWidth: number;
  startWidth: number;
  nextStartWidth: number;
}

@Component({
  selector: 'app-moodle-dashboard',
  templateUrl: './moodle-dashboard.component.html',
  styleUrl: './moodle-dashboard.component.scss',
})
export class MoodleDashboardComponent implements OnInit, OnDestroy {
  loading = true;
  refreshingFromMoodle = false;
  refreshProgress: MoodleRefreshStatus | null = null;
  private refreshStartedAt: string | null = null;
  private refreshPollHandle: number | null = null;
  activeTab: MoodleDashboardTab = 'courses';
  courses: CanonicalCourse[] = [];
  users: CanonicalUser[] = [];
  teachers: CanonicalTeacher[] = [];
  featureSets: FeatureSet[] = [];
  moodleSites: LmsRegistration[] = [];
  selectedMoodleRegistrationId: string | null = null;
  portalDozentEmails = new Set<string>();
  invitingTeacherIds = new Set<string>();
  searchCoursesInput = '';
  searchCourses = '';
  searchUsersInput = '';
  searchUsers = '';
  searchTeachersInput = '';
  searchTeachers = '';
  categoryFilter: string | null = null;
  categoryOptions: Array<{
    key?: string;
    registration_id?: string;
    id: number;
    label?: string | null;
    name?: string | null;
    path?: string | null;
    count?: number;
  }> = [];
  private teacherSupportLoaded = false;
  pageSize = 50;
  pageSizeOptions = [25, 50, 100];
  currentPage: Record<MoodleDashboardTab, number> = {
    courses: 0,
    users: 0,
    teachers: 0,
  };
  totalRows: Record<MoodleDashboardTab, number> = {
    courses: 0,
    users: 0,
    teachers: 0,
  };
  private readonly columnWidthStorageKey = 'al-moodle-dashboard-column-widths';
  private readonly defaultColumnWidths: MoodleDashboardColumnWidths = {
    courses: {
      name: 30,
      short: 14,
      category: 20,
      id: 8,
      sync: 12,
      responsible: 18,
      learners: 8,
      action: 5,
    },
    users: {
      name: 32,
      email: 31,
      id: 10,
      enrol: 11,
      sync: 11,
      action: 5,
    },
    teachers: {
      name: 28,
      email: 28,
      role: 17,
      enrol: 10,
      sync: 11,
      actionWide: 6,
    },
  };
  private readonly minColumnWidths: Partial<Record<MoodleDashboardColumnKey, number>> = {
    name: 14,
    short: 8,
    category: 12,
    id: 7,
    sync: 8,
    action: 4,
    email: 14,
    enrol: 7,
    role: 10,
    actionWide: 5,
  };
  columnWidths: MoodleDashboardColumnWidths = {
    courses: { ...this.defaultColumnWidths.courses },
    users: { ...this.defaultColumnWidths.users },
    teachers: { ...this.defaultColumnWidths.teachers },
  };
  // --- Sichtbare Spalten -----------------------------------------------------
  // Kuerzel und Moodle-Id sind ab Werk aus: sie beantworten selten eine Frage,
  // kosten aber die Breite, die Kursname, Verantwortliche und Kategorie
  // brauchen. Umschaltbar ueber "Spalten", gemerkt wie die Spaltenbreiten.
  private static readonly COURSE_COLUMNS: { key: MoodleDashboardColumnKey; label: string }[] = [
    { key: 'short', label: 'Kürzel' },
    { key: 'category', label: 'Kategorie' },
    { key: 'id', label: 'Moodle-ID' },
    { key: 'sync', label: 'Letzter Sync' },
    { key: 'responsible', label: 'Verantwortlich' },
    { key: 'learners', label: 'Lernende' },
  ];
  private static readonly COURSE_COLUMNS_HIDDEN_BY_DEFAULT: MoodleDashboardColumnKey[] = [
    'short',
    'id',
  ];
  private static readonly COLUMN_VISIBILITY_STORAGE_KEY = 'al-moodleDashboard-columns-v1';

  /** Filter auf Verantwortliche: null = alle, '__none__' = ohne. */
  responsibleFilter: string | null = null;

  /** Namen, die in der aktuellen Kursliste als verantwortlich vorkommen. */
  private responsibleOptionsFor: CanonicalCourse[] | null = null;
  private responsibleOptionsCache: string[] = [];

  get responsibleOptions(): string[] {
    if (this.responsibleOptionsFor === this.courses) {
      return this.responsibleOptionsCache;
    }
    this.responsibleOptionsFor = this.courses;
    const names = new Set<string>();
    for (const c of this.courses || []) {
      for (const r of c.responsibles || []) if (r?.name) names.add(r.name);
    }
    this.responsibleOptionsCache = [...names].sort((a, b) =>
      a.localeCompare(b, 'de'),
    );
    return this.responsibleOptionsCache;
  }

  private readonly destroy$ = new Subject<void>();

  @ViewChild('coursesTable') coursesTable?: { deselectAll: () => void };

  showColumnPicker = false;
  hiddenColumns = new Set<MoodleDashboardColumnKey>(
    MoodleDashboardComponent.COURSE_COLUMNS_HIDDEN_BY_DEFAULT,
  );

  get courseColumnOptions(): { key: MoodleDashboardColumnKey; label: string }[] {
    return MoodleDashboardComponent.COURSE_COLUMNS;
  }

  isColumnVisible(key: MoodleDashboardColumnKey): boolean {
    return !this.hiddenColumns.has(key);
  }

  toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  toggleColumn(key: MoodleDashboardColumnKey): void {
    if (this.hiddenColumns.has(key)) this.hiddenColumns.delete(key);
    else this.hiddenColumns.add(key);
    this.persistColumnVisibility();
  }


  private persistColumnVisibility(): void {
    try {
      localStorage.setItem(
        MoodleDashboardComponent.COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify([...this.hiddenColumns]),
      );
    } catch {
      // Privates Fenster / blockierter Speicher: die Auswahl gilt dann nur
      // fuer diese Sitzung. Kein Grund, die Seite scheitern zu lassen.
    }
  }

  private restoreColumnVisibility(): void {
    try {
      const raw = localStorage.getItem(MoodleDashboardComponent.COLUMN_VISIBILITY_STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (Array.isArray(stored)) this.hiddenColumns = new Set(stored);
    } catch {
      // Unlesbarer Eintrag: bei den Voreinstellungen bleiben.
    }
  }

  private activeColumnResize: MoodleColumnResizeState | null = null;
  private readonly onColumnResizeMove = (event: MouseEvent) => this.resizeColumn(event);
  private readonly onColumnResizeEnd = () => this.stopColumnResize();

  constructor(
    private moodleDashboard: MoodleDashboardService,
    private lmsRegistry: LmsRegistryService,
    private authService: AuthService,
    private dialog: NbDialogService,
    private featureSetService: FeatureSetService,
    private userService: UserService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private curriculum: CurriculumService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.restoreColumnWidths();
    this.restoreColumnVisibility();
    // Sprungziel aus einer Benachrichtigung: `?prereqCourseId=<unsere
    // courses.id>`.
    //
    // Laufend beobachtet, nicht einmal aus dem Snapshot gelesen: wer schon auf
    // dieser Seite steht und in der Glocke auf die Meldung klickt, wechselt
    // die Route nicht — Angular baut die Komponente dann nicht neu und
    // `ngOnInit` laeuft kein zweites Mal. Genau so fuehrte der Klick ins
    // Leere.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const id = params.get('prereqCourseId');
        if (!id) return;
        this.pendingPrereqCourseId = id;
        this.tryOpenPendingPrereq();
      });

    this.loadMoodleSites()
      // `refresh()` gibt kein Promise zurück, also direkt auf den Ladevorgang
      // warten — sonst liefe das Sprungziel, bevor die Kursliste da ist.
      .catch(() => undefined)
      .then(() => this.loadDashboardData())
      .then(() => this.tryOpenPendingPrereq());
  }

  /** Merker fuer ein Sprungziel, das noch auf die Kursliste wartet. */
  private pendingPrereqCourseId: string | null = null;

  /** Oeffnet die Verknuepfungen zu einer unserer Kurs-Ids, sobald es geht.
   *
   *  Wird zweimal versucht: wenn der Parameter kommt und wenn die Liste
   *  geladen ist. Welches von beidem zuerst passiert, haengt davon ab, ob man
   *  die Seite neu aufruft oder schon darauf steht.
   */
  private tryOpenPendingPrereq(): void {
    const id = this.pendingPrereqCourseId;
    if (!id || !this.courses?.length) return;

    const course = this.courses.find((c) => c.local_course_id === id);
    if (course) {
      this.pendingPrereqCourseId = null;
      // Parameter entfernen, sonst oeffnet ein Neuladen denselben Dialog
      // wieder, und ein zweiter Klick auf dieselbe Meldung taete nichts,
      // weil sich der Parameter nicht aendert.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { prereqCourseId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.openPrerequisites(course);
      return;
    }

    // Der Kurs steht auf einer anderen Seite der Liste. Statt still nichts zu
    // tun, sagen wir warum — eine Benachrichtigung, die ins Leere fuehrt, ist
    // schlimmer als eine, die erklaert.
    this.pendingPrereqCourseId = null;
    this.toastr.info(
      'Der Kurs steht nicht auf dieser Seite der Liste. Such ihn über das Suchfeld und öffne „Kursinhalte verknüpfen".',
      'Kursverknüpfung',
    );
  }

  ngOnDestroy(): void {
    this.clearRefreshPoll();
    this.removeColumnResizeListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  columnWidth(tab: MoodleDashboardTab, key: MoodleDashboardColumnKey): number {
    return this.columnWidths[tab]?.[key] ?? this.defaultColumnWidths[tab]?.[key] ?? 10;
  }

  isResizingTab(tab: MoodleDashboardTab): boolean {
    return this.activeColumnResize?.tab === tab;
  }

  startColumnResize(
    event: MouseEvent,
    tab: MoodleDashboardTab,
    key: MoodleDashboardColumnKey,
    nextKey: MoodleDashboardColumnKey,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    const table = (event.currentTarget as HTMLElement | null)?.closest('table');
    const tableWidth = table?.getBoundingClientRect().width || 0;
    if (!tableWidth) return;
    this.activeColumnResize = {
      tab,
      key,
      nextKey,
      startX: event.clientX,
      tableWidth,
      startWidth: this.columnWidth(tab, key),
      nextStartWidth: this.columnWidth(tab, nextKey),
    };
    window.addEventListener('mousemove', this.onColumnResizeMove);
    window.addEventListener('mouseup', this.onColumnResizeEnd);
  }

  resetColumnWidths(tab: MoodleDashboardTab): void {
    this.columnWidths = {
      ...this.columnWidths,
      [tab]: { ...this.defaultColumnWidths[tab] },
    };
    this.persistColumnWidths();
  }

  private resizeColumn(event: MouseEvent): void {
    if (!this.activeColumnResize) return;
    event.preventDefault();
    const resize = this.activeColumnResize;
    const deltaPercent = ((event.clientX - resize.startX) / resize.tableWidth) * 100;
    const totalPairWidth = resize.startWidth + resize.nextStartWidth;
    const minCurrent = this.minColumnWidths[resize.key] ?? 6;
    const minNext = this.minColumnWidths[resize.nextKey] ?? 6;
    const width = Math.min(
      Math.max(resize.startWidth + deltaPercent, minCurrent),
      totalPairWidth - minNext,
    );
    const nextWidth = totalPairWidth - width;
    this.columnWidths = {
      ...this.columnWidths,
      [resize.tab]: {
        ...this.columnWidths[resize.tab],
        [resize.key]: Number(width.toFixed(2)),
        [resize.nextKey]: Number(nextWidth.toFixed(2)),
      },
    };
  }

  private stopColumnResize(): void {
    if (!this.activeColumnResize) return;
    this.activeColumnResize = null;
    this.removeColumnResizeListeners();
    this.persistColumnWidths();
  }

  private removeColumnResizeListeners(): void {
    window.removeEventListener('mousemove', this.onColumnResizeMove);
    window.removeEventListener('mouseup', this.onColumnResizeEnd);
  }

  private restoreColumnWidths(): void {
    try {
      const raw = localStorage.getItem(this.columnWidthStorageKey);
      if (!raw) return;
      const stored = JSON.parse(raw) as Partial<MoodleDashboardColumnWidths>;
      this.columnWidths = {
        courses: { ...this.defaultColumnWidths.courses, ...(stored.courses || {}) },
        users: { ...this.defaultColumnWidths.users, ...(stored.users || {}) },
        teachers: { ...this.defaultColumnWidths.teachers, ...(stored.teachers || {}) },
      };
    } catch {
      this.columnWidths = {
        courses: { ...this.defaultColumnWidths.courses },
        users: { ...this.defaultColumnWidths.users },
        teachers: { ...this.defaultColumnWidths.teachers },
      };
    }
  }

  private persistColumnWidths(): void {
    try {
      localStorage.setItem(this.columnWidthStorageKey, JSON.stringify(this.columnWidths));
    } catch {
      // Non-critical UI preference.
    }
  }

  get canSelectMoodleSite(): boolean {
    const payload = this.authService.accessTokenPayload$.value;
    const role = (payload?.role || '').toLowerCase();
    if (['super_admin', 'admin', 'manager'].includes(role)) return true;
    const perms = (payload?.feature_set_permissions || []).map((p) => String(p).toUpperCase());
    return role === 'user' && !perms.includes('DOZENT');
  }

  private async loadMoodleSites(): Promise<void> {
    try {
      const sites = await firstValueFrom(this.lmsRegistry.listMoodleSites());
      this.moodleSites = (sites || []).filter((s) => !this.isSupersededMoodleSite(s));
      const stored = this.canSelectMoodleSite
        ? this.lmsRegistry.getStoredMoodleRegistrationId('moodle-dashboard')
        : null;
      const storedSite = stored ? this.moodleSites.find((s) => s.id === stored) : null;
      const operationalSites = this.moodleSites.filter((s) => this.isOperationalMoodleSite(s));
      const fallback =
        operationalSites.find((s) => s.is_primary) ||
        operationalSites[0] ||
        this.moodleSites.find((s) => s.is_primary) ||
        this.moodleSites[0] ||
        null;
      this.selectedMoodleRegistrationId = (storedSite || fallback)?.id || null;
      if (this.canSelectMoodleSite && this.selectedMoodleRegistrationId) {
        this.lmsRegistry.setStoredMoodleRegistrationId(
          'moodle-dashboard',
          this.selectedMoodleRegistrationId,
        );
      }
    } catch {
      this.moodleSites = [];
      this.selectedMoodleRegistrationId = null;
    }
  }

  private isSupersededMoodleSite(site: LmsRegistration): boolean {
    return (
      String(site.health_status || '').toLowerCase() === 'superseded' ||
      site.connection_status === 'superseded'
    );
  }

  private isOperationalMoodleSite(site: LmsRegistration): boolean {
    if (this.isSupersededMoodleSite(site) || site.disabled_at) return false;
    return !['disconnected', 'token_revoked', 'paused'].includes(
      String(site.connection_status || '').toLowerCase(),
    );
  }

  onMoodleSiteChange(registrationId: string): void {
    this.selectedMoodleRegistrationId = registrationId;
    this.lmsRegistry.setStoredMoodleRegistrationId('moodle-dashboard', registrationId);
    this.categoryFilter = null;
    this.currentPage = { courses: 0, users: 0, teachers: 0 };
    this.refresh();
  }

  onTabChange(tabId: string): void {
    if (!['courses', 'users', 'teachers'].includes(tabId) || this.activeTab === tabId) return;
    this.activeTab = tabId as MoodleDashboardTab;
    this.refresh();
  }

  refresh(): void {
    void this.loadDashboardData();
  }

  get refreshProgressPercent(): number {
    const progress = this.refreshActiveProgress;
    if (progress && progress.total > 0) {
      return Math.round(((progress.done + progress.failed) / progress.total) * 100);
    }
    return this.refreshProgress?.percent ?? 0;
  }

  get refreshProgressLabel(): string {
    if (!this.refreshProgress) return '';
    const active = this.refreshActiveProgress;
    const courseMeta = this.refreshCourseMetaProgress;
    const enrollments = this.refreshEnrollmentProgress;
    if (courseMeta && courseMeta.done + courseMeta.failed < courseMeta.total) {
      const key = courseMeta.failed > 0
        ? 'pages.moodle.refreshProgress.checkingCoursesWithErrors'
        : 'pages.moodle.refreshProgress.checkingCourses';
      return this.translate.instant(key, {
        done: courseMeta.done,
        failed: courseMeta.failed,
        total: courseMeta.total,
        percent: this.progressPercentFor(courseMeta),
      });
    }
    if (enrollments && enrollments.total > 0 && this.refreshProgress.state !== 'completed' && this.refreshProgress.state !== 'completed_with_errors') {
      const key = enrollments.failed > 0
        ? 'pages.moodle.refreshProgress.syncingParticipantsWithErrors'
        : 'pages.moodle.refreshProgress.syncingParticipants';
      return this.translate.instant(key, {
        done: enrollments.done,
        failed: enrollments.failed,
        total: enrollments.total,
        percent: this.progressPercentFor(enrollments),
      });
    }
    if (this.refreshProgress.state === 'completed') {
      if (!enrollments || enrollments.total === 0) {
        return this.translate.instant('pages.moodle.refreshProgress.completedCourses');
      }
      return this.translate.instant('pages.moodle.refreshProgress.completed', {
        total: active?.total ?? this.refreshProgress.total,
        percent: this.progressPercentFor(active),
      });
    }
    if (this.refreshProgress.state === 'completed_with_errors') {
      return this.translate.instant('pages.moodle.refreshProgress.completedWithErrors', {
        done: active?.done ?? this.refreshProgress.done,
        failed: active?.failed ?? this.refreshProgress.failed,
        total: active?.total ?? this.refreshProgress.total,
        percent: this.progressPercentFor(active),
      });
    }
    if (this.refreshProgress.state === 'running_with_errors') {
      return this.translate.instant('pages.moodle.refreshProgress.runningWithErrors', {
        done: active?.done ?? this.refreshProgress.done,
        failed: active?.failed ?? this.refreshProgress.failed,
        total: active?.total ?? this.refreshProgress.total,
        percent: this.progressPercentFor(active),
      });
    }
    return this.translate.instant('pages.moodle.refreshProgress.running', {
      done: active?.done ?? this.refreshProgress.done,
      total: active?.total ?? this.refreshProgress.total,
      percent: this.progressPercentFor(active),
    });
  }

  private progressPercentFor(progress?: NonNullable<MoodleRefreshStatus['by_kind']>[string] | null): number {
    if (progress && progress.total > 0) {
      return Math.round(((progress.done + progress.failed) / progress.total) * 100);
    }
    return this.refreshProgress?.percent ?? 0;
  }

  get refreshProgressStatus(): 'primary' | 'success' | 'warning' | 'danger' {
    if (!this.refreshProgress) return 'primary';
    if (this.refreshProgress.state === 'completed') return 'success';
    if (this.refreshProgress.state === 'completed_with_errors') return 'warning';
    if (this.refreshProgress.state === 'running_with_errors') return 'warning';
    return 'primary';
  }

  private get refreshCourseMetaProgress(): NonNullable<MoodleRefreshStatus['by_kind']>[string] | null {
    return this.refreshProgress?.by_kind?.['course_meta'] ?? null;
  }

  private get refreshEnrollmentProgress(): NonNullable<MoodleRefreshStatus['by_kind']>[string] | null {
    return this.refreshProgress?.by_kind?.['course_enrollments'] ?? null;
  }

  private get refreshActiveProgress(): NonNullable<MoodleRefreshStatus['by_kind']>[string] | null {
    const courseMeta = this.refreshCourseMetaProgress;
    if (courseMeta && courseMeta.done + courseMeta.failed < courseMeta.total) {
      return courseMeta;
    }
    return this.refreshEnrollmentProgress ?? courseMeta;
  }

  private loadDashboardData(tab: MoodleDashboardTab = this.activeTab): Promise<void> {
    this.loading = true;
    const registrationId = this.canSelectMoodleSite ? this.selectedMoodleRegistrationId : null;

    let request: Promise<void>;
    if (tab === 'courses') {
      request = firstValueFrom(this.moodleDashboard.listCourses(registrationId, {
        limit: this.pageSize,
        offset: this.currentPage.courses * this.pageSize,
        search: this.searchCourses,
        categoryKey: this.categoryFilter,
      })).then((coursesResp) => {
        this.courses = coursesResp.data ?? [];
        this.totalRows.courses = coursesResp.total ?? coursesResp.pagination?.total ?? this.courses.length;
        this.categoryOptions = coursesResp.categories ?? [];
      });
    } else if (tab === 'users') {
      request = firstValueFrom(this.moodleDashboard.listUsers(registrationId, {
        limit: this.pageSize,
        offset: this.currentPage.users * this.pageSize,
        search: this.searchUsers,
      })).then((usersResp) => {
        this.users = usersResp.data ?? [];
        this.totalRows.users = usersResp.total ?? usersResp.pagination?.total ?? this.users.length;
      });
    } else {
      const supportRequest = this.teacherSupportLoaded
        ? Promise.resolve()
        : Promise.all([
            firstValueFrom(this.featureSetService.loadFeatureSets()),
            firstValueFrom(this.moodleDashboard.listAssignableDozents()).catch(() => []),
          ]).then(([featureSets, portalDozents]) => {
            this.featureSets = featureSets ?? [];
            this.portalDozentEmails = new Set(
              (portalDozents ?? [])
                .map((u) => (u.email || '').trim().toLowerCase())
                .filter(Boolean),
            );
            this.teacherSupportLoaded = true;
          });
      request = Promise.all([
        firstValueFrom(this.moodleDashboard.listTeachers(registrationId, {
          limit: this.pageSize,
          offset: this.currentPage.teachers * this.pageSize,
          search: this.searchTeachers,
        })),
        supportRequest,
      ]).then(([teachersResp]) => {
        this.teachers = teachersResp.data ?? [];
        this.totalRows.teachers = teachersResp.total ?? teachersResp.pagination?.total ?? this.teachers.length;
      });
    }

    return request
      .catch(() => undefined)
      .finally(() => {
        this.loading = false;
      });
  }

  async refreshFromMoodle(): Promise<void> {
    if (this.refreshingFromMoodle) return;
    this.refreshingFromMoodle = true;
    this.clearRefreshPoll();
    const registrationId = this.canSelectMoodleSite ? this.selectedMoodleRegistrationId : null;
    try {
      const trigger = await firstValueFrom(this.moodleDashboard.refreshCanonical(registrationId));
      this.refreshStartedAt = this.refreshStartedAtFromTrigger(trigger);
      if (this.triggerRefreshedCourseMeta(trigger)) {
        this.refresh();
      }
      this.refreshProgress = {
        state: 'running',
        total: 0,
        done: 0,
        failed: 0,
        pending: 0,
        running: 0,
        queued: 0,
        percent: 0,
      };
      this.pollRefreshStatus();
    } catch (err: any) {
      this.refreshingFromMoodle = false;
      this.toastr.danger(
        this.backendErrorMessage(err, 'pages.moodle.refreshFailed'),
        this.translate.instant('pages.moodle.refresh'),
      );
    }
  }

  private triggerRefreshedCourseMeta(trigger: any): boolean {
    if (Array.isArray(trigger?.refreshed_kinds) && trigger.refreshed_kinds.includes('course_meta')) {
      return true;
    }
    if (trigger?.course_meta_stats) {
      return true;
    }
    if (Array.isArray(trigger?.queued)) {
      return trigger.queued.some((item: any) => this.triggerRefreshedCourseMeta(item));
    }
    return false;
  }

  private refreshStartedAtFromTrigger(trigger: any): string {
    if (trigger?.next_due_at) return trigger.next_due_at;
    const first = Array.isArray(trigger?.queued) ? trigger.queued[0] : null;
    return first?.next_due_at || new Date().toISOString();
  }

  private pollRefreshStatus(): void {
    const registrationId = this.canSelectMoodleSite ? this.selectedMoodleRegistrationId : null;
    firstValueFrom(this.moodleDashboard.getRefreshStatus(registrationId, this.refreshStartedAt))
      .then((status) => {
        this.refreshProgress = status;
        if (
          status.state === 'idle' ||
          status.state === 'completed' ||
          status.state === 'completed_with_errors'
        ) {
          this.refreshingFromMoodle = false;
          this.clearRefreshPoll();
          this.refresh();
          const ok = status.state === 'idle' || status.state === 'completed';
          const key = ok
            ? 'pages.moodle.refreshSuccess'
            : 'pages.moodle.refreshPartialSuccess';
          if (ok) {
            this.toastr.success(
              this.translate.instant(key),
              this.translate.instant('pages.moodle.refresh'),
            );
          } else {
            this.toastr.warning(
              this.translate.instant(key),
              this.translate.instant('pages.moodle.refresh'),
            );
          }
          return;
        }
        this.refreshPollHandle = window.setTimeout(() => this.pollRefreshStatus(), 2500);
      })
      .catch((err) => {
        this.refreshingFromMoodle = false;
        this.clearRefreshPoll();
        this.toastr.danger(
          this.backendErrorMessage(err, 'pages.moodle.refreshFailed'),
          this.translate.instant('pages.moodle.refresh'),
        );
      });
  }

  private clearRefreshPoll(): void {
    if (this.refreshPollHandle != null) {
      window.clearTimeout(this.refreshPollHandle);
      this.refreshPollHandle = null;
    }
  }

  private backendErrorMessage(err: any, fallbackKey: string): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      if (typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message;
      }
      if (typeof detail.code === 'string' && detail.code.trim()) {
        return detail.code;
      }
    }
    return this.translate.instant(fallbackKey);
  }

  // Die drei folgenden Getter hängen im Template (`[data]`, `[columns]`,
  // `[rowActions]`, `<nb-option *ngFor>`). Ein Getter, der jedes Mal ein neues
  // Array baut, liefert bei jedem Change-Detection-Lauf eine neue Identität —
  // `app-table` sieht dann einen geänderten Input, wirft die gerenderten
  // Knoten weg und baut sie neu. Ein Klick überlebt das nicht: `mousedown`
  // trifft einen Knoten, der bis `mouseup` ersetzt wurde, also entsteht gar
  // kein `click`. Deshalb wird gemerkt, woraus das Ergebnis entstand, und
  // dieselbe Instanz zurückgegeben, solange sich daran nichts ändert.
  private filteredCoursesFor: CanonicalCourse[] | null = null;
  private filteredCoursesFilter: string | null = null;
  private filteredCoursesCache: CanonicalCourse[] = [];

  get filteredCourses(): CanonicalCourse[] {
    if (
      this.filteredCoursesFor === this.courses &&
      this.filteredCoursesFilter === this.responsibleFilter
    ) {
      return this.filteredCoursesCache;
    }
    this.filteredCoursesFor = this.courses;
    this.filteredCoursesFilter = this.responsibleFilter;
    this.filteredCoursesCache = this.computeFilteredCourses();
    return this.filteredCoursesCache;
  }

  private computeFilteredCourses(): CanonicalCourse[] {
    // Suche und Kategorie filtert der Server (Seitenweise). Der
    // Verantwortlichen-Filter arbeitet auf der geladenen Seite — die Namen
    // kommen mit der Zeile, eine eigene Serverrunde wäre dafür unnötig.
    if (!this.responsibleFilter) return this.courses;
    if (this.responsibleFilter === '__none__') {
      return this.courses.filter((c) => !(c.responsibles || []).length);
    }
    return this.courses.filter((c) =>
      (c.responsibles || []).some((r) => r?.name === this.responsibleFilter),
    );
  }

  get pagedCourses(): CanonicalCourse[] {
    return this.filteredCourses;
  }

  get filteredUsers(): CanonicalUser[] {
    return this.users;
  }

  get pagedUsers(): CanonicalUser[] {
    return this.filteredUsers;
  }

  get filteredTeachers(): CanonicalTeacher[] {
    return this.teachers;
  }

  get pagedTeachers(): CanonicalTeacher[] {
    return this.filteredTeachers;
  }

  get coursesEmptyMessageKey(): string {
    return this.searchCourses || this.categoryFilter
      ? 'pages.moodle.noCourseResults'
      : 'pages.moodle.coursesEmpty';
  }

  get usersEmptyMessageKey(): string {
    return this.searchUsers
      ? 'pages.moodle.noUserResults'
      : 'pages.moodle.usersEmpty';
  }

  get teachersEmptyMessageKey(): string {
    return this.searchTeachers
      ? 'pages.moodle.noTeacherResults'
      : 'pages.moodle.teachersEmpty';
  }

  resetPage(tab: MoodleDashboardTab): void {
    this.currentPage[tab] = 0;
    this.refresh();
  }

  applySearch(tab: MoodleDashboardTab): void {
    if (tab === 'courses') {
      this.searchCourses = this.searchCoursesInput.trim();
    } else if (tab === 'users') {
      this.searchUsers = this.searchUsersInput.trim();
    } else {
      this.searchTeachers = this.searchTeachersInput.trim();
    }
    this.resetPage(tab);
  }

  clearSearch(tab: MoodleDashboardTab): void {
    if (tab === 'courses') {
      this.searchCoursesInput = '';
      this.searchCourses = '';
    } else if (tab === 'users') {
      this.searchUsersInput = '';
      this.searchUsers = '';
    } else {
      this.searchTeachersInput = '';
      this.searchTeachers = '';
    }
    this.resetPage(tab);
  }

  onCourseFilterChange(): void {
    this.resetPage('courses');
  }

  onPageChange(tab: MoodleDashboardTab, page: number): void {
    const total = this.totalRowsForTab(tab);
    const maxPage = Math.max(Math.ceil(total / this.pageSize) - 1, 0);
    this.currentPage[tab] = Math.max(0, Math.min(page, maxPage));
    this.refresh();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = { courses: 0, users: 0, teachers: 0 };
    this.refresh();
  }

  private totalRowsForTab(tab: MoodleDashboardTab): number {
    return this.totalRows[tab] ?? 0;
  }

  openCourse(c: CanonicalCourse): void {
    this.dialog.open(MoodleDashboardCourseDetailComponent, {
      context: { course: c },
      hasBackdrop: true,
    });
  }

  // --- Zeilenmenü der Kurse -------------------------------------------------
  // Ersetzt drei lose Icons in der Aktionszelle. Nebular hängt das Menü in den
  // CDK-Overlay-Container ausserhalb der Tabelle — ein selbstgebauter
  // Aufklapper würde an der Tabellenkante abgeschnitten.

  /** Die Einträge einer Zeile. Ohne lokale Kurszeile entfallen die zwei
   *  unteren — beide hängen an unserem Kurs, nicht am kanonischen Moodle-Kurs,
   *  und würden sonst einen Dialog öffnen, der nur scheitern kann. */
  /** Spalten fuer `app-table`, gefiltert nach dem Spalten-Waehler.
   *  Gemerkt, solange Spaltenauswahl und Sprache gleich bleiben — siehe die
   *  Begruendung bei `filteredCourses`. */
  private courseColumnsKey = '';
  private courseColumnsCache: AlTableColumn[] = [];

  get courseTableColumns(): AlTableColumn[] {
    const key =
      [...this.hiddenColumns].sort().join(',') + '|' + this.translate.currentLang;
    if (key === this.courseColumnsKey && this.courseColumnsCache.length) {
      return this.courseColumnsCache;
    }
    this.courseColumnsKey = key;
    this.courseColumnsCache = this.buildCourseTableColumns();
    return this.courseColumnsCache;
  }

  private buildCourseTableColumns(): AlTableColumn[] {
    const all: { key: MoodleDashboardColumnKey | 'name'; col: AlTableColumn }[] = [
      { key: 'name', col: { key: 'fullname', title: this.translate.instant('pages.moodle.col.fullname'), width: '28%', sortable: true, map: (v: any) => v || '—' } },
      { key: 'short', col: { key: 'shortname', title: this.translate.instant('pages.moodle.col.shortname'), map: (v: any) => v || '—' } },
      { key: 'category', col: { key: 'category_name', title: this.translate.instant('pages.moodle.col.category'), map: (_: any, row: any) => this.courseCategoryLabel(row) } },
      { key: 'id', col: { key: 'moodle_course_id', kind: 'number', title: this.translate.instant('pages.moodle.col.moodleId'), map: (v: any) => '#' + v } },
      { key: 'sync', col: { key: 'last_synced_at', kind: 'date', title: this.translate.instant('pages.moodle.col.lastSync'), map: (v: any) => this.formatRelative(v) } },
      { key: 'responsible', col: { key: 'responsibles', title: 'Verantwortlich', map: (_: any, row: any) => this.responsiblesTitle(row) || '—' } },
      { key: 'learners', col: { key: 'participant_count', kind: 'number', title: 'Lernende', sortable: true, map: (v: any) => v ?? 0 } },
    ];
    return all
      .filter((e) => e.key === 'name' || this.isColumnVisible(e.key as MoodleDashboardColumnKey))
      .map((e) => e.col);
  }

  /** Zeilen- und Sammelaktionen. `isMultiAction` blendet den Eintrag in die
   *  Auswahlleiste ein, die `app-table` selbst mitbringt.
   *  Einmal gebaut: die Liste haengt an nichts, was sich zur Laufzeit aendert,
   *  und eine neue Identitaet je Change-Detection-Lauf macht die Knoepfe
   *  unklickbar (siehe `filteredCourses`). */
  private courseRowActionsCache?: AlTableRowAction[];

  get courseRowActions(): AlTableRowAction[] {
    return (this.courseRowActionsCache ??= this.buildCourseRowActions());
  }

  private buildCourseRowActions(): AlTableRowAction[] {
    return [
      { key: 'structure', title: 'Kursstruktur ansehen', icon: 'list-outline' },
      { key: 'difficulty', title: 'Schwierigkeits-Report', icon: 'bar-chart-outline' },
      {
        key: 'responsibles', title: 'Verantwortliche verwalten', icon: 'people-outline',
        // Haengt an UNSEREM Kurs, den es nur bei gespiegeltem Moodle-Kurs gibt.
        disabled: (row: any) => !row?.local_course_id,
        tooltip: (row: any) => row?.local_course_id ? '' : 'Dieser Moodle-Kurs ist bei uns noch nicht gespiegelt.',
      },
      {
        key: 'prerequisites', title: 'Kursinhalte verknüpfen', icon: 'link-2-outline',
        disabled: (row: any) => !row?.local_course_id,
        tooltip: (row: any) => row?.local_course_id ? '' : 'Dieser Moodle-Kurs ist bei uns noch nicht gespiegelt.',
      },
      // Nur in der Auswahlleiste: einzeln gibt es dafuer schon
      // "Verantwortliche verwalten", und im Zeilenmenue waeren sie eine
      // dritte Variante derselben Sache, die beim Klick nichts tut.
      {
        key: 'assignMe', title: 'Mich eintragen',
        icon: 'person-done-outline', isMultiAction: true, multiOnly: true,
      },
      {
        key: 'assignSomeone', title: 'Person zuordnen …',
        icon: 'people-outline', isMultiAction: true, multiOnly: true,
      },
    ];
  }

  onCourseTableEvent(e: { event: string; data: any }): void {
    if (e.event === 'rowAction') {
      this.runCourseAction(e.data.row, e.data.action?.key);
      return;
    }
    if (e.event === 'multiAction') {
      const rows: CanonicalCourse[] = e.data.rows || [];
      if (e.data.action?.key === 'assignMe') this.assignResponsible(rows, null);
      if (e.data.action?.key === 'assignSomeone') this.pickAndAssignResponsible(rows);
    }
  }


  /** Vollständige Liste als Titel, damit das Kürzen nichts verbirgt. */
  responsiblesTitle(c: CanonicalCourse): string | null {
    const list = c.responsibles || [];
    return list.length ? list.map((r) => r.name || '—').join(', ') : null;
  }

  private runCourseAction(c: CanonicalCourse, action: string): void {
    const stop = new Event('none');
    switch (action) {
      case 'structure':
        this.openCourseStructure(c, stop);
        break;
      case 'difficulty':
        this.openCourseDifficulty(c, stop);
        break;
      case 'responsibles':
        this.openResponsibles(c);
        break;
      case 'prerequisites':
        this.openPrerequisites(c);
        break;
    }
  }

  /** Eine Person fuer mehrere Kurse auf einmal benennen.
   *
   *  `userId = null` heisst "mich" — der haeufigste Fall: jemand macht sich
   *  ueberall selbst verantwortlich, damit Verknuepfungen ueberhaupt
   *  bestaetigt werden koennen. Kurse ohne eigene Kurszeile werden
   *  uebersprungen und am Ende genannt, statt die ganze Aktion scheitern zu
   *  lassen.
   */
  private assignResponsible(rows: CanonicalCourse[], userId: string | null): void {
    const me = this.authService.accessTokenPayload$.value?.user_id;
    const target = userId || me;
    if (!target) {
      // Frueher ein stilles `return` — der Klick sah dann aus, als haette der
      // Knopf keine Funktion. Eine Aktion, die nichts tut, muss sagen warum.
      this.toastr.danger(
        'Die eigene Nutzerkennung liegt nicht vor. Bitte neu anmelden.',
        'Verantwortliche');
      return;
    }

    const withCourse = rows.filter((r) => r.local_course_id);
    const skipped = rows.length - withCourse.length;
    if (!withCourse.length) {
      this.toastr.warning(
        'Keiner der gewählten Kurse ist bei uns gespiegelt.', 'Verantwortliche');
      return;
    }

    let done = 0;
    let failed = 0;
    const finish = () => {
      if (done + failed < withCourse.length) return;
      if (failed) {
        this.toastr.warning(
          `${done} von ${withCourse.length} Kursen zugeordnet, ${failed} fehlgeschlagen.`,
          'Verantwortliche');
      } else {
        this.toastr.success(
          `${done} Kurs${done === 1 ? '' : 'e'} zugeordnet` +
            (skipped ? ` · ${skipped} übersprungen (nicht gespiegelt)` : ''),
          'Verantwortliche');
      }
      this.coursesTable?.deselectAll();
      this.refresh();
    };

    for (const row of withCourse) {
      this.curriculum.grantResponsible(row.local_course_id!, target).subscribe({
        next: () => { done++; finish(); },
        // Schon benannt zaehlt als Erfolg: die Sammelaktion soll nicht an
        // einem Kurs scheitern, der laengst passt.
        error: (e: any) => { (e?.status === 409 ? done++ : failed++); finish(); },
      });
    }
  }

  /** Erst die Person waehlen, dann zuordnen. */
  private pickAndAssignResponsible(rows: CanonicalCourse[]): void {
    this.userService.listUsers().subscribe((users: any[]) => {
      const options = (users || []).map((u) => ({
        value: u.id,
        label: (u.name || '').trim() ? `${u.name} · ${u.email}` : u.email,
      }));
      if (!options.length) {
        this.toastr.warning('Keine Nutzer gefunden.', 'Verantwortliche');
        return;
      }
      this.dialog
        .open(ConfirmDialogComponent, {
          context: {
            title: 'Verantwortliche/n zuordnen',
            message: `Für ${rows.length} gewählte${rows.length === 1 ? 'n' : ''} Kurs${rows.length === 1 ? '' : 'e'}:`,
            options,
            selectedOption: options[0].value,
            confirmLabel: 'Zuordnen',
          },
          hasBackdrop: true,
        })
        .onClose.subscribe((userId: any) => {
          if (userId) this.assignResponsible(rows, String(userId));
        });
    });
  }

  private openResponsibles(c: CanonicalCourse): void {
    const localId = c.local_course_id;
    if (!localId) return;
    this.dialog
      .open(CourseResponsiblesDialogComponent, {
        context: { courseId: localId, courseName: c.fullname || c.shortname || '' },
        hasBackdrop: true,
      })
      .onClose.subscribe((changed: boolean) => {
        // The column shows the names, so a change has to be re-read; the
        // menu cache keys on that count and would otherwise stay stale.
        if (changed) this.refresh();
      });
  }

  private openPrerequisites(c: CanonicalCourse): void {
    const localId = c.local_course_id;
    if (!localId) return;
    // The picker lists our own mirrored courses — a link can only point at a
    // course we actually hold content for.
    const options = this.courses
      .map((row) => ({
        id: row.local_course_id || '',
        name: row.fullname || row.shortname || '',
        // Ohne verantwortliche Person weist das Backend die Anfrage ab (es
        // gaebe niemanden, der zustimmen koennte). Die Liste hat die Namen
        // schon dabei, also kann die Auswahl das vorher sagen statt den
        // Nutzer in eine Fehlermeldung laufen zu lassen.
        hasResponsible: !!(row.responsibles || []).length,
      }))
      .filter((o) => !!o.id);
    this.curriculum.mayEditCourse(localId).subscribe({
      next: (may) => this.showPrerequisites(c, localId, options, may),
      error: () => this.showPrerequisites(c, localId, options, false),
    });
  }

  private showPrerequisites(
    c: CanonicalCourse,
    localId: string,
    courses: { id: string; name: string }[],
    canRequest: boolean,
  ): void {
    this.dialog.open(CoursePrerequisitesDialogComponent, {
      context: {
        courseId: localId,
        courseName: c.fullname || c.shortname || '',
        courses,
        canRequest,
      },
      hasBackdrop: true,
    });
  }

  openCourseStructure(c: CanonicalCourse, event: Event): void {
    event.stopPropagation();
    this.dialog.open(ParsedCourseStructureDialogComponent, {
      context: { course: c },
      hasBackdrop: true,
    });
  }

  openCourseDifficulty(c: CanonicalCourse, event: Event): void {
    event.stopPropagation();
    this.dialog.open(CourseDifficultyDialogComponent, {
      context: { course: c },
      hasBackdrop: true,
    });
  }

  openUser(u: CanonicalUser): void {
    this.dialog.open(MoodleDashboardUserDetailComponent, {
      context: { user: u },
      hasBackdrop: true,
    });
  }

  openLearnerProfile(u: CanonicalUser, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!u.learner_id) {
      this.openUser(u);
      return;
    }
    this.dialog.open(LearnerProfileDialogComponent, {
      context: {
        learnerId: u.learner_id,
        learnerName: this.userName(u),
        learnerEmail: u.email || '',
      },
      hasBackdrop: true,
    });
  }

  userName(u: CanonicalUser): string {
    return participantPseudonym({
      id: u.id,
      learner_user_id: u.learner_id,
      external_user_id: String(u.moodle_user_id),
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
    });
  }

  teacherName(u: CanonicalTeacher): string {
    const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
    return name || u.email || `Moodle-User #${u.moodle_user_id}`;
  }

  courseCategoryLabel(c: CanonicalCourse): string {
    return c.category_path || c.category_name || '—';
  }

  teacherRoleLabel(u: CanonicalTeacher): string {
    return (u.role_shortnames || []).join(', ') || '—';
  }

  isPortalDozent(u: CanonicalTeacher): boolean {
    const email = (u.email || '').trim().toLowerCase();
    return !!email && this.portalDozentEmails.has(email);
  }

  isInvitingTeacher(u: CanonicalTeacher): boolean {
    return this.invitingTeacherIds.has(u.id);
  }

  learnerProfileTooltip(u: CanonicalUser): string {
    return this.translate.instant(
      u.learner_id
        ? 'pages.moodle.viewLearnerProfile'
        : 'pages.moodle.noLearnerProfile',
    );
  }

  teacherInviteTooltip(u: CanonicalTeacher): string {
    if (!u.email) return this.translate.instant('pages.moodle.inviteTeacherNoEmail');
    if (this.isPortalDozent(u)) return this.translate.instant('pages.moodle.inviteTeacherAlreadyPortal');
    if (!this.dozentFeatureSet) return this.translate.instant('pages.moodle.inviteTeacherNoFeatureSet');
    return this.translate.instant('pages.moodle.inviteTeacher');
  }

  inviteTeacher(u: CanonicalTeacher, event?: MouseEvent): void {
    event?.stopPropagation();
    const email = (u.email || '').trim().toLowerCase();
    const featureSet = this.dozentFeatureSet;

    if (!email) {
      this.toastr.warning(
        this.translate.instant('pages.moodle.inviteTeacherNoEmail'),
        this.translate.instant('pages.moodle.inviteTeacher'),
      );
      return;
    }
    if (!featureSet) {
      this.toastr.danger(
        this.translate.instant('pages.moodle.inviteTeacherNoFeatureSet'),
        this.translate.instant('pages.moodle.inviteTeacher'),
      );
      return;
    }
    if (this.isPortalDozent(u) || this.isInvitingTeacher(u)) return;

    this.invitingTeacherIds.add(u.id);
    firstValueFrom(this.userService.inviteUser({
      name: this.teacherName(u),
      email,
      role: UserRoles.User,
      feature_set_id: featureSet.id,
    } as any))
      .then(() => {
        this.portalDozentEmails.add(email);
        this.toastr.success(
          this.translate.instant('pages.moodle.inviteTeacherSuccess', { email }),
          this.translate.instant('pages.moodle.inviteTeacher'),
        );
      })
      .catch((err) => {
        const detail = err?.error?.detail || this.translate.instant('pages.moodle.inviteTeacherFailed');
        this.toastr.danger(detail, this.translate.instant('pages.moodle.inviteTeacher'));
      })
      .finally(() => this.invitingTeacherIds.delete(u.id));
  }

  private get dozentFeatureSet(): FeatureSet | undefined {
    return (
      this.featureSets.find((fs) =>
        (fs.permissions || []).some((perm) => String(perm).toUpperCase() === 'DOZENT'),
      ) ||
      this.featureSets.find((fs) => (fs.name || '').trim().toLowerCase() === 'dozent')
    );
  }

  formatRelative(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'gerade eben';
    if (min < 60) return `vor ${min} Min.`;
    const h = Math.floor(min / 60);
    if (h < 24) return `vor ${h} Std.`;
    const days = Math.floor(h / 24);
    return `vor ${days} Tg.`;
  }

  trackCourse = (_: number, c: CanonicalCourse) => c.id;
  trackUser = (_: number, u: CanonicalUser) => u.id;
  trackTeacher = (_: number, u: CanonicalTeacher) => u.id;
}
